import { Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from '../../auth/auth.service';
import { FormHelper } from '../../form/form-helper';
import { FormHelperFactory } from '../../form/form-helper.factory';
import { SnackbarService } from '../../service/snackbar.service';

export interface ConfirmRegistrationForm {
  email: FormControl<string>;
}

@Component({
  selector: 'app-email-link-login-page',
  templateUrl: './email-link-login-page.component.html',
  styleUrls: ['./email-link-login-page.component.scss'],
})
export class EmailLinkLoginPage implements OnInit {
  loginLink = '';
  isValidLoginLink = false;
  isEmailKnown = false;
  isSaving = false;

  form: FormGroup<ConfirmRegistrationForm>;
  formHelper: FormHelper<ConfirmRegistrationForm>;

  constructor(
    private snackBar: SnackbarService,
    private formHelperFactory: FormHelperFactory,
    private authService: AuthService,
    private router: Router,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.loginLink = window.location.href;
    this.isValidLoginLink = this.authService.isValidSignInWithEmailLink(this.loginLink);
    this.isEmailKnown = this.authService.getStoredEmailForSignIn() !== null;

    const fb = new FormBuilder();
    this.form = fb.group(
      {
        email: fb.nonNullable.control('', this.isEmailKnown ? [] : [Validators.required, Validators.email]),
      },
      { updateOn: 'submit' },
    );
    this.formHelper = this.formHelperFactory.create(this.form);

    // Redirect after successful login
    this.authService.currentUser$.pipe(takeUntilDestroyed()).subscribe(user => {
      if (user) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        const { ref } = queryParams;
        const forwardedParams = { ref };

        this.router.navigate([user.redirectUrlAfterLogin], { queryParams: forwardedParams });
      }
    });
  }

  async ngOnInit() {
    // User registered with the same browser and email is known, attempt to log in directly.
    if (this.isValidLoginLink && this.isEmailKnown) {
      await this.signIn(this.authService.getStoredEmailForSignIn()!);
    }
  }

  async submitForm() {
    this.formHelper.trackSubmitted();

    if (!this.form.valid) {
      return;
    }

    this.isSaving = true;
    await this.signIn(this.form.controls.email.value);
    this.isSaving = false;
  }

  async signIn(email: string) {
    try {
      await this.authService.signInWithEmailLink(this.loginLink, email);
    } catch (e) {
      this.snackBar.showError(this.translateService.instant('PAGE.EMAIL_LINK_LOGIN.NOTIFICATION.LOGIN_FAILED'));
    }
  }
}
